/**
 * UpgradePlanDialog — confirmação de upgrade pra quem JÁ TEM assinatura
 * paga ativa. Em vez de abrir um novo checkout (que o backend bloqueia com
 * "assinatura ativa"), chama POST /billing/upgrade: troca plano + preço na
 * Asaas e no DB na hora, sem novo pagamento imediato.
 *
 * Fluxo: PlanPickerDialog → (tenant com sub ativa paga) → este dialog →
 * confirmar → toast + invalidate billing queries.
 */
import { useTranslation } from 'react-i18next';
import { ArrowRight, Loader2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { useToast } from '@/components/ui/toast';
import { useUpgradePlan } from '@/api/queries';
import { formatPriceBRL } from '@my360/shared';

export interface UpgradeTarget {
  slug: string;
  name: string;
  priceCents: number;
}

interface UpgradePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Target plan picked by the user */
  target: UpgradeTarget | null;
  /** Current plan slug (display only) */
  currentPlan: string;
  /** Current plan price in cents, when known (display only) */
  currentPriceCents?: number | null;
  /** Called after a successful upgrade (refresh auth/billing in the parent) */
  onSuccess?: () => void;
}

export function UpgradePlanDialog({
  open,
  onOpenChange,
  target,
  currentPlan,
  currentPriceCents,
  onSuccess,
}: UpgradePlanDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const upgradeMutation = useUpgradePlan();

  if (!target) return null;

  const handleConfirm = () => {
    upgradeMutation.mutate(target.slug, {
      onSuccess: () => {
        toast.success(
          t('billingUi:upgrade.toast.successTitle'),
          t('billingUi:upgrade.toast.successDesc', { name: target.name }),
        );
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : '';
        toast.error(
          t('billingUi:upgrade.toast.errorTitle'),
          msg.includes('higher plan')
            ? t('billingUi:upgrade.toast.downgradeBlocked')
            : msg || t('billingUi:upgrade.toast.errorDesc'),
        );
      },
    });
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => {
        if (!o && upgradeMutation.isPending) return;
        onOpenChange(o);
      }}
      size="sm"
      title={
        <span className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)] text-white shadow-md">
            <TrendingUp className="h-4 w-4" />
          </span>
          <span className="flex flex-col">
            <span
              className="text-base tracking-tight font-semibold"
              style={{ color: 'var(--color-text)' }}
            >
              {t('billingUi:upgrade.title', { name: target.name })}
            </span>
            <span
              className="text-[11px] font-normal"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('billingUi:upgrade.subtitle')}
            </span>
          </span>
        </span>
      }
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={upgradeMutation.isPending}
          >
            {t('billingUi:upgrade.cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={upgradeMutation.isPending}>
            {upgradeMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('billingUi:upgrade.upgrading')}
              </>
            ) : (
              t('billingUi:upgrade.confirm')
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Old → new plan summary */}
        <div className="rounded-lg bg-[var(--color-surface-elevated)] border border-[var(--color-border)] p-4 flex items-center justify-between gap-3">
          <div className="min-w-0 text-center flex-1">
            <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-semibold">
              {t('billingUi:upgrade.fromLabel')}
            </div>
            <div
              className="text-sm font-semibold capitalize truncate"
              style={{ color: 'var(--color-text)' }}
            >
              {currentPlan}
            </div>
            {currentPriceCents != null && (
              <div className="text-[11px] text-[var(--color-text-muted)] tabular-nums">
                {formatPriceBRL(currentPriceCents)}
                {t('billingUi:common.perMonth')}
              </div>
            )}
          </div>
          <ArrowRight className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
          <div className="min-w-0 text-center flex-1">
            <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-semibold">
              {t('billingUi:upgrade.toLabel')}
            </div>
            <div
              className="text-sm font-semibold capitalize truncate"
              style={{ color: 'var(--color-text)' }}
            >
              {target.name}
            </div>
            <div className="text-[11px] font-semibold text-[var(--color-primary)] tabular-nums">
              {formatPriceBRL(target.priceCents)}
              {t('billingUi:common.perMonth')}
            </div>
          </div>
        </div>

        <p className="text-[11px] text-[var(--color-text-muted)] text-center leading-relaxed">
          {t('billingUi:upgrade.note', { price: formatPriceBRL(target.priceCents) })}
        </p>
      </div>
    </ResponsiveDialog>
  );
}
